import React, { useEffect, useMemo, useState } from "react";
import { Input, Select, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { useWbsName } from "../../shared/useWbsCodes";
import {
  fmtINR,
  fmtINRCompact,
  fmtQty,
  num,
  perUnit,
} from "./machineryShared";

// Machinery register ACTIVITY -> TASK: bold activity group rows, expanding to
// the TASKS under them, with every machinery source folded into each row
// (Mucking + Tools + Equipment) and a ₹/unit beside every money column.
//
// WHY THIS EXISTS BESIDE THE NORM-WISE REGISTER: that one groups by the NORM's
// activity, and equipment (deployment) norms have none — they are keyed by BOQ
// code — so every equipment rupee collapses into a single "no activity"
// bucket. This view groups by the TASK's activity, which all three sources
// have, so a face finally shows its Mucking and its Equipment side by side and
// the activity total is the real cost of that face.
//
// SERVER-DRIVEN, same contract as the sibling table: 10 activities/page,
// debounced search (activity OR task name/sequence), Total Cost sort. Groups
// arrive collapsed and expandedRowKeys resets on every fetch.
//
// RATES ARE CLIENT-SIDE: cost ÷ work quantity, the one denominator this report
// family agrees on. The server sends workQuantity as '' (not '0') when a row
// has no basis, so a missing rate is always a blank, never a fake zero.

const PAGE_SIZE = 10;
const GROUP_ROW_BG = "#f5f7fa";
const INK = "#022c57";
const MUTED = "#8c8c8c";

type SortDir = "asc" | "desc";

interface ApiTaskRow {
  taskId?: number;
  taskName?: string;
  taskSequenceId?: string;
  workQuantity?: string;
  workUom?: string;
  normCost?: string;
  toolsCost?: string;
  equipmentCost?: string;
  totalCost?: string;
  lineCount?: number;
  phaseId?: number;
  phaseName?: string;
}

interface ApiGroup {
  activityId?: number;
  activityName?: string;
  phaseId?: number;
  phaseName?: string;
  workQuantity?: string;
  workUom?: string;
  normCost?: string;
  toolsCost?: string;
  equipmentCost?: string;
  totalCost?: string;
  taskCount?: number;
  tasks?: ApiTaskRow[];
}

interface Row {
  key: string;
  isGroup: boolean;
  /** Identity for the equipment drill-down — one of these is set. */
  activityId?: number;
  taskId?: number;
  name: string;
  subLabel?: string;
  qty: number;
  /** '' = no single UOM (mixed) or no basis — rate must render blank. */
  uom: string;
  normCost: number;
  toolsCost: number;
  equipmentCost: number;
  totalCost: number;
  taskCount?: number;
  lineCount?: number;
  children?: Row[];
}

interface Props {
  projectId?: number | string;
  phaseId?: number;
  activityId?: number;
  head?: string;
  refreshTick?: number;
  grandTotal: number;
  /** Opens the equipment drill-down for the clicked row's scope. */
  onEquipmentClick?: (scope: {
    name: string;
    activityId?: number;
    taskId?: number;
    expectedAmount: number;
  }) => void;
}

const MachineryActivityTaskTable: React.FC<Props> = ({
  projectId,
  phaseId,
  activityId,
  head,
  refreshTick,
  grandTotal,
  onEquipmentClick,
}) => {
  const wbsName = useWbsName();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);
  const [groups, setGroups] = useState<ApiGroup[]>([]);
  const [activityCount, setActivityCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [phaseId, activityId, head]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    setExpandedRowKeys([]);
    threeMReportApis
      .get3MMachineryActivityTasks(projectId, {
        phaseId,
        activityId,
        head,
        search: debouncedSearch || undefined,
        sortDir,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((response) => {
        if (cancelled) return;
        if (response?.status) {
          setGroups(response?.data?.groups || []);
          setActivityCount(num(response?.data?.activityCount));
        } else {
          setGroups([]);
          setActivityCount(0);
        }
      })
      .catch((err) => {
        console.error("Error loading machinery activity-task register:", err);
        if (!cancelled) {
          setGroups([]);
          setActivityCount(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    phaseId,
    activityId,
    head,
    debouncedSearch,
    sortDir,
    page,
    refreshTick,
  ]);

  const rows: Row[] = useMemo(
    () =>
      groups.map((group) => ({
        key: `A-${group.activityId ?? 0}`,
        isGroup: true,
        activityId: group.activityId ?? 0,
        name: group.activityName || "(no activity)",
        subLabel: group.phaseName ? wbsName(group.phaseName) : undefined,
        qty: num(group.workQuantity),
        uom: group.workUom || "",
        normCost: num(group.normCost),
        toolsCost: num(group.toolsCost),
        equipmentCost: num(group.equipmentCost),
        totalCost: num(group.totalCost),
        taskCount: num(group.taskCount),
        children: (group.tasks || []).map((task) => ({
          key: `A-${group.activityId ?? 0}-T-${task.taskId ?? 0}`,
          isGroup: false,
          taskId: task.taskId ?? 0,
          name: task.taskName || `Task #${task.taskId}`,
          subLabel: task.taskSequenceId || undefined,
          qty: num(task.workQuantity),
          uom: task.workUom || "",
          normCost: num(task.normCost),
          toolsCost: num(task.toolsCost),
          equipmentCost: num(task.equipmentCost),
          totalCost: num(task.totalCost),
          lineCount: num(task.lineCount),
        })),
      })),
    [groups, wbsName]
  );

  // A money cell with its ₹/unit underneath. Blank (never 0) when the row has
  // no quantity to divide by, or when the row's tasks disagree on a unit — a
  // rate with no unit to label it is worse than no rate.
  const moneyCell = (value: number, row: Row, bold = false) => {
    if (value === 0)
      return <span style={{ color: "#c8cfd8" }}>—</span>;
    const rate = row.uom ? perUnit(value, row.qty) : null;
    return (
      <div>
        <Tooltip title={fmtINR(value)}>
          <span
            style={{
              fontWeight: bold ? 600 : 400,
              color: bold ? INK : undefined,
              fontVariantNumeric: "tabular-nums",
              cursor: "default",
            }}
          >
            {fmtINRCompact(value)}
          </span>
        </Tooltip>
        {rate !== null ? (
          <div
            style={{
              fontSize: 11,
              color: "#8094ae",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {fmtINR(rate)}/{row.uom}
          </div>
        ) : null}
      </div>
    );
  };

  const columns: ColumnsType<Row> = [
    {
      title: "Activity / Task",
      key: "name",
      render: (_: any, row) => (
        <>
          <div
            style={{
              fontWeight: row.isGroup ? 600 : 500,
              color: row.isGroup ? INK : undefined,
            }}
            title={row.name}
          >
            {row.isGroup ? wbsName(row.name) : row.name}
          </div>
          {row.subLabel ? (
            <div style={{ fontSize: 11, color: MUTED }}>{row.subLabel}</div>
          ) : null}
          {row.isGroup ? (
            <div style={{ fontSize: 11, color: MUTED }}>
              {row.taskCount} {row.taskCount === 1 ? "task" : "tasks"}
            </div>
          ) : null}
        </>
      ),
    },
    {
      title: "Work Qty",
      key: "qty",
      width: 130,
      align: "right",
      render: (_: any, row) =>
        row.qty > 0 && row.uom ? (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {fmtQty(row.qty)}
            <span style={{ color: MUTED, fontSize: 11 }}> {row.uom}</span>
          </span>
        ) : (
          <Tooltip
            title={
              row.isGroup
                ? "This activity's tasks are measured in different units, so a combined quantity — and any rate from it — would have no unit to label it"
                : "This task has no work quantity, so no rate can be derived"
            }
          >
            <Tag style={{ marginRight: 0 }}>
              {row.isGroup && row.qty > 0 ? "Mixed" : "—"}
            </Tag>
          </Tooltip>
        ),
    },
    {
      title: "Mucking",
      key: "norm",
      width: 130,
      align: "right",
      render: (_: any, row) => moneyCell(row.normCost, row),
    },
    {
      // Clickable: opens the drill-down of WHICH MACHINES burned this money,
      // under which norms and against which cost heads. Only when there is
      // equipment cost to explain — a dash is not a link.
      title: "Equipment",
      key: "equipment",
      width: 130,
      align: "right",
      render: (_: any, row) => {
        const cell = moneyCell(row.equipmentCost, row);
        if (!onEquipmentClick || row.equipmentCost === 0) return cell;
        return (
          <Tooltip title="View the machine-wise breakdown">
            <span
              role="button"
              tabIndex={0}
              onClick={() =>
                onEquipmentClick({
                  name: row.name,
                  activityId: row.isGroup ? row.activityId : undefined,
                  taskId: row.isGroup ? undefined : row.taskId,
                  expectedAmount: row.equipmentCost,
                })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onEquipmentClick({
                    name: row.name,
                    activityId: row.isGroup ? row.activityId : undefined,
                    taskId: row.isGroup ? undefined : row.taskId,
                    expectedAmount: row.equipmentCost,
                  });
                }
              }}
              style={{
                cursor: "pointer",
                color: "#2557d6",
                textDecoration: "underline",
                textUnderlineOffset: 2,
                display: "inline-block",
              }}
            >
              {cell}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Tools",
      key: "tools",
      width: 120,
      align: "right",
      render: (_: any, row) => moneyCell(row.toolsCost, row),
    },
    {
      title: "Total",
      key: "total",
      width: 140,
      align: "right",
      render: (_: any, row) => moneyCell(row.totalCost, row, true),
    },
    {
      title: "Share",
      key: "share",
      width: 90,
      align: "right",
      render: (_: any, row) => (
        <span style={{ color: MUTED, fontVariantNumeric: "tabular-nums" }}>
          {grandTotal > 0
            ? `${((row.totalCost / grandTotal) * 100).toFixed(1)}%`
            : "—"}
        </span>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 10 }}>
        <h6 style={{ fontWeight: 600, marginBottom: 2, color: INK }}>
          Activity-wise Machinery Register — by Task
        </h6>
        <span style={{ color: MUTED, fontSize: 13 }}>
          Every machinery source per task — expand an activity for its
          task-wise cost and ₹/unit
        </span>
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "nowrap",
          gap: 10,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <Input
          allowClear
          size="large"
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          placeholder="Search activity or task name"
          style={{ flex: "1 1 320px", minWidth: 320 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select<SortDir>
          size="large"
          value={sortDir}
          onChange={(value) => {
            setSortDir(value);
            setPage(1);
          }}
          style={{ flex: "0 0 232px", width: 232 }}
          options={[
            {
              value: "desc",
              label: (
                <span>
                  <SortDescendingOutlined style={{ marginRight: 6 }} />
                  Total Cost: High → Low
                </span>
              ),
            },
            {
              value: "asc",
              label: (
                <span>
                  <SortAscendingOutlined style={{ marginRight: 6 }} />
                  Total Cost: Low → High
                </span>
              ),
            },
          ]}
        />
        <span
          style={{
            color: MUTED,
            fontSize: 13,
            marginLeft: "auto",
            whiteSpace: "nowrap",
          }}
        >
          {activityCount} activit{activityCount === 1 ? "y" : "ies"}
        </span>
      </div>
      <Table<Row>
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={rows}
        loading={loading}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
        }}
        onRow={(row) =>
          row.isGroup ? { style: { background: GROUP_ROW_BG } } : {}
        }
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: activityCount,
          showSizeChanger: false,
          showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} activities`,
        }}
        onChange={(pagination) => setPage(pagination.current || 1)}
        scroll={{ x: 1050 }}
      />
    </>
  );
};

export default MachineryActivityTaskTable;
